/* Redux-Toolkit */
import { createAsyncThunk } from "@reduxjs/toolkit";

/* Axios */
import { HttpStatusCode, isAxiosError } from "axios";
import agentAxiosInterceptor from "src/utils/helper/agent-axios-interceptor";

/* Constants */
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";

/* Interfaces */
import {
  AgentChatMessage,
  AgentChatResponse,
  AgentModelsResponse,
} from "src/shared/interfaces/api/AgentChat";

/**
 * Fetch the LLM models the current user is allowed to use.
 *
 * Rejects with "forbidden" when the user lacks the
 * insights_agent role, and with "error" when the agent is
 * not reachable (e.g. not part of the deployment).
 */
export const fetchAllowedAgentModels = createAsyncThunk(
  "agentChat/fetchAllowedAgentModels",
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await agentAxiosInterceptor.get<AgentModelsResponse>(
          "/models/allowed"
        );
      return response.data;
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === HttpStatusCode.Forbidden
      ) {
        return rejectWithValue("forbidden");
      }
      return rejectWithValue("error");
    }
  }
);

interface SendAgentChatQuestionParams {
  question: string;
  history: AgentChatMessage[];
  model: string | null;
}

/**
 * Send a question to the insights-agent chat endpoint.
 */
export const sendAgentChatQuestion = createAsyncThunk(
  "agentChat/sendAgentChatQuestion",
  async (
    params: SendAgentChatQuestionParams,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await agentAxiosInterceptor.post<AgentChatResponse>(
          "/chat/",
          {
            question: params.question,
            history: params.history,
            model: params.model ?? undefined,
          }
        );
      return response.data.answer;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        const detail = (
          error.response.data as { detail?: unknown }
        )?.detail;
        if (typeof detail === "string") {
          return rejectWithValue(detail);
        }
      }
      return rejectWithValue(
        ErrorMessage.UNEXPECTED_ERROR
      );
    }
  }
);
